import { useEffect } from "react";
import { logSession, LogLevel } from "utils/sentryLogger";

// Keeps the screen awake for the whole session: iPads otherwise dim and lock
// during the quiet stretches (Solo Time, waiting for peers). iOS releases the
// lock whenever the page leaves the foreground, so it is re-requested on
// every return. Safari also refuses a request that is not backed by a user
// gesture ("NotAllowedError: Permission was denied"), and the mount-time
// request never is, so the next tap or key press retries while no lock is
// held. Best effort — where the API is missing or every request is refused,
// default dimming simply applies.
export const useWakeLock = () => {
  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.wakeLock?.request) return undefined;

    let sentinel = null;
    let pending = false;
    let cancelled = false;
    let acquiredLogged = false;
    let failureLogged = false;

    const request = async () => {
      if (sentinel || pending || document.visibilityState !== "visible") return;
      pending = true;
      try {
        const lock = await navigator.wakeLock.request("screen");
        if (cancelled) {
          lock.release();
          return;
        }
        sentinel = lock;
        lock.addEventListener("release", () => {
          if (sentinel === lock) sentinel = null;
        });
        if (!acquiredLogged) {
          acquiredLogged = true;
          logSession("Screen wake lock acquired", {});
        }
      } catch (e) {
        if (failureLogged) return;
        failureLogged = true;
        logSession("Screen wake lock request failed", {
          level: LogLevel.WARNING,
          errorName: e?.name,
          errorMessage: e?.message,
          visibilityState: document.visibilityState,
        });
      } finally {
        pending = false;
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") request();
    };
    const onGesture = () => request();

    request();
    document.addEventListener("visibilitychange", onVisibilityChange);
    document.addEventListener("pointerdown", onGesture, { capture: true });
    document.addEventListener("keydown", onGesture, { capture: true });

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibilityChange);
      document.removeEventListener("pointerdown", onGesture, { capture: true });
      document.removeEventListener("keydown", onGesture, { capture: true });
      sentinel?.release();
      sentinel = null;
    };
  }, []);
};
